import { useEffect, useMemo, useState } from "react";
import CustomSelect from "../../../../../CommonComponents/CustomSelect";
import useContacts from "../../../../hooks/useContacts";
import { ScheduleTask } from "../../../../types";
import MissionService from "../../../../../../service/MissionService";
import useUrlParams from "../../../../hooks/useUrlParams";
import ModalInput from "../../../../custom_components/ModalInput";
import { showToastMessage } from "../../../../../../utils/commonFunctions";
import useFiles from "../../../../hooks/useFiles";
import InputDropdown from "../../../../../CommonComponents/InputDrodown/InputDropdown";
import useScenario from "../../../../hooks/useScenario";
import SecurityConfigForm from "./SecurityConfigForm";
import { SECRET_KEY_TASK_TYPES } from "../../../../../CommonComponents/security-configuration/helperFunctions";
import useSecurityConfig from "../../../../hooks/useSecurityConfig";
import { SCENARIO_DEPLOYMENT_TYPES } from "../../../../../simulate/constants";

export interface Option {
  label: string;
  value: any;
  disabled?: boolean;
  hideOption?: boolean;
  style?: string;
}

const TaskType = [
  { label: "Uplink File", value: "file" },
  { label: "Ad hoc Task", value: "adhoc" },
  { label: "Upload Schedule", value: "schedule" },
  { label: "Set OBC Current time", value: "set_obc_curr_time" },
  { label: "Upload & Activate Secret Key", value: "upload_activate_secret_key" },
  { label: "Upload & Deactivate Secret Key", value: "upload_deactivate_secret_key" },
  { label: "Activate Secret Key", value: "activate_secret_key" },
  { label: "Deactivate Secret Key", value: "deactivate_secret_key" },
];

// Common for all console dropdowns (Destination-style)
const DROPDOWN_CONTAINER_CLASS = "left__panel__body__input__dropdown__container left__panel__body__input__custom__dropdown";
const DROPDOWN_CONTAINER_CLASS_BORDER = `${DROPDOWN_CONTAINER_CLASS} left__panel__body_input__border`;
const CONSOLE_SELECT_PROPS = {
  background: "rgba(10,10,10,0.65)",
  controllHeight: "28px",
};

const TaskForm = ({ onFormSubmit }) => {
  const { sat_grp_id } = useUrlParams();
  const { allOnBoardSchedule } = useContacts();
  const { scenario } = useScenario();
  const { setSecurityConfigFormData, resetSecurityConfigFormData, EncripTionOptions } = useSecurityConfig()
  const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase();
  const isTTCCommandCenter = deploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER || deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE;
  const [taskType, setTaskType] = useState(TaskType[0]);
  const [allTaskOptions, setAllTaskOptions] = useState<{ label: string; value: any }[]>([]);
  const [selected_task, set_selected_task] = useState<{ label: string; value: any } | null>(null);
  const [selected_schedule, set_selected_schedule] = useState<{ label: string; value: ScheduleTask } | null>(null);

  const [destinationId, setDestinationId] = useState<{ label: any, value: any } | null>(null);
  const [destinationIdOptions, setDestinationIdOptions] = useState<any[] | null>()
  const [fileIdOptionsFiltered, setFileIdOptionsFiltered] = useState<any[] | null>()
  const [showFileId, setShowFileId] = useState<boolean>(false)

  const [mtuValue, setMtuValue] = useState<any>(isTTCCommandCenter ? 600 : 990);
  const [delayValue, setDelayValue] = useState<any>(isTTCCommandCenter ? 40 : 10);
  const [activityTimeoutValue, setActivityTimeoutValue] = useState<any>(60);
  const [connectionTimeoutValue, setConnectionTimeoutValue] = useState<any>(75);


  //File id related states
  const [ftmFileId, setFtmFileId] = useState<{ label: any, value: any } | null>({ label: '1-OBC firmware', value: 1 });


  const fileIdMap: Record<number, { label: string; value: any }[]> = {
    141: [
      { label: '1 - OBC firmware file', value: 1 },
      { label: '2 - OBC NVM binary file', value: 2 }
    ],

    144: [
      { label: '1 - EPS MCU1 firmware', value: 1 },
      { label: '2 - EPS MCU2 firmware', value: 2 },
      { label: '3 - EPS MCU3 firmware', value: 3 },
      { label: '4 - EPS MCU4 firmware', value: 4 }
    ],

    148: [
      { label: '1 - IQS Firmware binary one', value: 1 },
      { label: '2 - IQS Firmware binary two', value: 2 },
      { label: '3 - IQS Firmware binary three', value: 3 }
    ],

    // 143 and 134 do NOT require a fileId → no entries here
  };

  useEffect(() => {
    const fileIdOptions = destinationId ? fileIdMap[destinationId?.value?.Id] : undefined;
    setFileIdOptionsFiltered(fileIdOptions)
    setFtmFileId(fileIdOptions?.[0] ?? null)
    setShowFileId(!!fileIdOptions?.length)
  }, [destinationId])

  const [isLossDetection, setIsLossDetection] = useState(true);
  const [fileName, setFileName] = useState("");
  const [fileObject, setFileObject] = useState<{
    uniqueId: string,
    fileType: string,
    fileName: string,
    fileStatus: string,
    lastModified: string
  } | null>(null);
  const { allACPStorageFiles, allDestinationIds } = useFiles();

  const fileOptions = useMemo(() => {
    const files = Array.from(allACPStorageFiles?.['uplink-files']?.values() || []);
    return files.map((file) => ({ label: file?.fileName ?? '', value: file }));
  }, [allACPStorageFiles]);

  const selectedFileOption = fileObject ? { label: fileObject.fileName, value: fileObject } : null;
  const [isAdvanceOptionEnable, setIsAdvanceOptionEnable] = useState<boolean>(false)

  //GET DESTINATION IDS
  useEffect(() => {
    setDestinationIdOptions(allDestinationIds)
  }, [allDestinationIds])

  //When console open to task by default set the tasktype as file
  useEffect(() => {
    if (sessionStorage.getItem('taskType') === null) {
      sessionStorage.setItem('taskType', 'file')
    }
  }, [])

  useEffect(() => {
    if (!isAdvanceOptionEnable) {
      setFtmFileId(fileIdOptionsFiltered?.[0] ?? null)
      setIsLossDetection(true)
      setMtuValue(isTTCCommandCenter ? 600 : 990)
      setDelayValue(isTTCCommandCenter ? 40 : 10)
      setActivityTimeoutValue(60)
      setConnectionTimeoutValue(75)
    }
  }, [isAdvanceOptionEnable])

  //GET SCHEDULES
  const PLANED_SCHEDULE = useMemo(() => {
    return (allOnBoardSchedule?.available || [])
      .filter((schedule) => schedule?.status !== "EXPIRED")
      .map((schedule) => ({
        label: schedule?.scheduleName || "",
        value: schedule || "",
      }));
  }, [allOnBoardSchedule]);


  //HANDLE TASKTYPE CHANGE (FILE, SCHEDULE, PAYLOAD, ADHOC)
  const handleTypeChange = (e) => {
    clear_previous_data(e);
    setTaskType(e);
    sessionStorage.setItem("taskType", e.value);
    if (SECRET_KEY_TASK_TYPES.includes(e.value)) {
      const activeFlag = e.value.includes('deactivate') ? false : true;
      setSecurityConfigFormData({
        taskType: e,
        keyType: null,
        activationFlag: activeFlag,
        keyValue: "",
        keyExchangeType: 'key',
        loading: false,
        encriptionEnabled: true,
        encriptionKey: EncripTionOptions[0],
      })
    } else {
      resetSecurityConfigFormData()
    }
  }

  //DECIMALDS NOT ALLOWED IN INPUT
  const handleDecimal = (e) => {
    if (e.key === '.' || e.key === 'e') {
      showToastMessage('error', 'Decimals are not allowed')
      e.preventDefault();
    }
  }

  const handleTaskChange = (e) => {
    set_selected_task(e);
    sessionStorage.setItem("selected_task", JSON.stringify(e?.value));
  }

  const handleScheduleChange = (e) => {
    set_selected_schedule(e);
    sessionStorage.setItem("selected_schedule", JSON.stringify(e?.value));
  }

  const handleFileSelect = (opt: { label: string; value: any } | null) => {
    if (opt?.value) {
      setFileObject(opt.value);
      setFileName(opt.value?.fileName ?? '');
    } else {
      setFileObject(null);
      setFileName('');
    }
  };

  const get_adhoc_task = async () => {
    if (!sat_grp_id) return;
    try {
      const response = await MissionService.getAllTask(sat_grp_id);
      const all_task: any[] = response.data;
      if (!all_task) return;
      const adhoc_tasks: { label: string; value: any }[] = response.data
        .filter((task: any) => task.taskType === "Adhoc")
        .map((task: any) => ({ label: task.taskName, value: task }));
      setAllTaskOptions(adhoc_tasks);
    } catch {
      showToastMessage("error", "Failed to load adhoc tasks");
    } finally {
      const selected_task = sessionStorage.getItem("selected_task") ? JSON.parse(sessionStorage.getItem("selected_task") || "{}") : null;
      const selected_schedule = sessionStorage.getItem("selected_schedule") ? JSON.parse(sessionStorage.getItem("selected_schedule") || "{}") : null;
      set_selected_task(
        selected_task && {
          label: selected_task?.taskName,
          value: selected_task,
        }
      );
      set_selected_schedule(
        selected_schedule && {
          label: selected_schedule?.ScheduleName,
          value: selected_schedule,
        }
      );
    }
  };

  useEffect(() => {
    get_adhoc_task();
  }, [sat_grp_id]);

  useEffect(() => {
    const isValid = !!(fileObject && destinationId && mtuValue && delayValue && activityTimeoutValue && connectionTimeoutValue); //add condition to check number and toast message
    const formData = {
      fileName,
      fileObject,
      fileId: fileObject?.uniqueId,
      destinationId: destinationId ? Number(destinationId?.value?.Id ?? destinationId?.value) : NaN,
      mtuValue: Number(mtuValue),
      delayValue: Number(delayValue),
      activityTimeoutValue: Number(activityTimeoutValue),
      connectionTimeoutValue: Number(connectionTimeoutValue),
      isLossDetection,
      ftmFileId: ftmFileId ? Number(ftmFileId.value) : 1,
    };

    onFormSubmit(isValid, formData);
  }, [fileName, destinationId, mtuValue, delayValue, activityTimeoutValue, connectionTimeoutValue, isLossDetection, fileObject, ftmFileId?.value]);

  const clear_previous_data = (taskType) => {
    if (taskType?.label !== "Uplink File") {
      setFileName("");
      setFileObject(null);
      setDestinationId(null);
      setMtuValue(isTTCCommandCenter ? 600 : 990);
      setFtmFileId({ label: '1-OBC firmware', value: 1 })
      setDelayValue(isTTCCommandCenter ? 40 : 10);
      setActivityTimeoutValue(60);
      setConnectionTimeoutValue(75);
      setIsLossDetection(true);
    }
    if (taskType?.label !== "Ad hoc Task") {
      set_selected_task(null);
      sessionStorage.removeItem('selected_task')
    }
    if (taskType?.label !== "Upload Schedule") {
      set_selected_schedule(null);
      sessionStorage.removeItem('selected_schedule')
    }
  };

  return (
    <div className="left__panel__body__form__section__container modal__scrollbar__white">
      {/* TASK TYPE SELECTION */}
      <div className={DROPDOWN_CONTAINER_CLASS_BORDER}>
        <span className="input__label">Type</span>
        <CustomSelect
          options={TaskType}
          value={taskType}
          placeholder="Select Task Type"
          onChange={(data) => { handleTypeChange(data) }}
          {...CONSOLE_SELECT_PROPS}
          isSearchable={false}
        />
      </div>
      {/* TASK TYPE -> FILE */}
      {taskType?.value === "file" && (
        <>
          <div className={DROPDOWN_CONTAINER_CLASS_BORDER}>
            <span className="input__label">File</span>
            <CustomSelect
              options={fileOptions}
              value={selectedFileOption}
              placeholder="File"
              onChange={handleFileSelect}
              {...CONSOLE_SELECT_PROPS}
              isSearchable={true}
            />
          </div>

          <div className={DROPDOWN_CONTAINER_CLASS_BORDER}>
            <span className="input__label">Destination</span>
            <CustomSelect
              options={destinationIdOptions || []}
              value={destinationId}
              placeholder="Destination"
              onChange={(opt) => setDestinationId(opt)}
              {...CONSOLE_SELECT_PROPS}
              isSearchable={true}
            />
          </div>

          <div className="d-flex w-100 justify-content-end cursor-pointer"><div onClick={() => setIsAdvanceOptionEnable(prev => !prev)}>{`${isAdvanceOptionEnable ? '-' : '+'} Advance`}</div></div>

          {showFileId && (
            <div className={`left__panel__body__input__dropdown__container ${isAdvanceOptionEnable ? '' : 'd-none'}`}>
              <span className="input__label">File Id</span>
              <div className="input__box__container seventy__width" style={{ width: "14.06vw" }}>
                <InputDropdown
                  allOptions={fileIdOptionsFiltered ?? []}
                  placeholder="File ID"
                  setFinalValue={setFtmFileId}
                  defaultValue={ftmFileId}
                  resetKey={`${fileIdOptionsFiltered?.length}-${isAdvanceOptionEnable}`}
                />
              </div>
            </div>
          )}

          <div className={`left__panel__body__input__option__container ${isAdvanceOptionEnable ? '' : 'd-none'}`}>
            <span className="input__label">MTU</span>
            <div className="input__box__container seventy__width with__unit">
              <ModalInput
                unit="B"
                value={mtuValue}
                handleChange={(value: string | number) => setMtuValue(String(value))}
                placeHolder="bytes"
                inputType="number"
                onKeyDown={(e) => { handleDecimal(e) }}
              />
            </div>
          </div>
          <div className={`left__panel__body__input__option__container ${isAdvanceOptionEnable ? '' : 'd-none'}`}>
            <span className="input__label">Delay</span>
            <div className="input__box__container seventy__width with__unit">
              <ModalInput
                unit="ms"
                value={delayValue}
                handleChange={(value: string | number) => setDelayValue(String(value))}
                placeHolder="e.g 1200"
                inputType="number"
                onKeyDown={(e) => { handleDecimal(e) }}
              />
            </div>
          </div>
          <div className={`left__panel__body__input__option__container ${isAdvanceOptionEnable ? '' : 'd-none'}`}>
            <span className="input__label">Activity Timeout</span>
            <div className="input__box__container seventy__width with__unit">
              <ModalInput
                unit="sec"
                value={activityTimeoutValue}
                handleChange={(value: string | number) => setActivityTimeoutValue(value)}
                placeHolder="e.g 14"
                inputType="number"
                onKeyDown={(e) => { handleDecimal(e) }}
              />
            </div>
          </div>
          <div className={`left__panel__body__input__option__container ${isAdvanceOptionEnable ? '' : 'd-none'}`}>
            <span className="input__label">Connection Timeout</span>
            <div className="input__box__container seventy__width with__unit">
              <ModalInput
                unit="sec"
                value={connectionTimeoutValue}
                handleChange={(value: string | number) => setConnectionTimeoutValue(value)}
                placeHolder="e.g 14"
                inputType="number"
                onKeyDown={(e) => { handleDecimal(e) }}
              />
            </div>
          </div>
          <div className={`left__panel__body__input__option__container mt-1 ${isAdvanceOptionEnable ? '' : 'd-none'}`} style={{ paddingBottom: "8px" }}>
            <span className="input__label">Loss Detection</span>
            <button className={`custom__toggle__btn__container ${isLossDetection && "active"}`} onClick={() => setIsLossDetection(!isLossDetection)} type="button"></button>
          </div>
        </>
      )}

      {taskType?.value === "adhoc" && (
        <div className={DROPDOWN_CONTAINER_CLASS}>
          <span className="input__label">Task</span>
          <CustomSelect
            options={allTaskOptions}
            value={selected_task}
            placeholder="Select Task"
            onChange={(e) => { handleTaskChange(e) }}
            {...CONSOLE_SELECT_PROPS}
            isSearchable={false}
          />
        </div>
      )}
      {taskType?.value === "schedule" && (
        <div className={DROPDOWN_CONTAINER_CLASS}>
          <span className="input__label">Schedule</span>
          <CustomSelect
            options={PLANED_SCHEDULE}
            value={selected_schedule}
            placeholder="Select Schedule"
            onChange={(e) => { handleScheduleChange(e) }}
            {...CONSOLE_SELECT_PROPS}
            isSearchable={false}
          />
        </div>
      )}

      {SECRET_KEY_TASK_TYPES.includes(taskType?.value) && (
        <SecurityConfigForm
          DROPDOWN_CONTAINER_CLASS={DROPDOWN_CONTAINER_CLASS}
          CONSOLE_SELECT_PROPS={CONSOLE_SELECT_PROPS}
        />
      )}
    </div>
  );
};

export default TaskForm;
